import React from 'react';
import {
  ModalFloatingCloseBtn,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { LoadingAnimation } from '../../components/LoadingAnimation';
import { PageWrapper } from '../../components/PageWrapper';
import { TransparentModal } from '../../components/TransparentModal';
import { useNavigateBack } from '../../navigation';
import type { SweetpassPaymentMethodEditScreenProps } from '../../navigation/AppNavigation.props';
import { SweetpassPaymentMethodEditScreenContent } from './SweetpassPaymentMethodEditScreenContent';
import { useSweetpassPaymentMethodEdit } from './useSweetpassPaymentMethodEdit';

export const SweetpassPaymentMethodEditScreen = ({
  route,
}: SweetpassPaymentMethodEditScreenProps) => {
  const { paymentMethodId } = route.params;

  const { isMobile } = useBreakpointHelpers();

  const {
    isSweetpassEnabled,
    hasActiveSubscription,
    paymentMethod,
    isSweetpassPaymentMethodLoading,
    setActive,
    remove,
    isSettingActive,
    isRemoving,
  } = useSweetpassPaymentMethodEdit({
    paymentMethodId,
  });

  const navigateBack = useNavigateBack();

  React.useLayoutEffect(() => {
    if (!paymentMethodId) {
      navigateBack();
    }
  }, [navigateBack, paymentMethodId]);

  const handlePressSetActive = React.useCallback(async () => {
    try {
      await setActive();
      navigateBack();
    } catch {
      // eslint-disable-next-line no-console
      console.log('Error setting payment method active');
    }
  }, [navigateBack, setActive]);

  const handlePressRemove = React.useCallback(async () => {
    try {
      await remove();
      navigateBack();
    } catch {
      // eslint-disable-next-line no-console
      console.log('Error removing payment method');
    }
  }, [navigateBack, remove]);

  return (
    <TransparentModal size="xsmall" onClose={navigateBack}>
      {isMobile ? null : <ModalFloatingCloseBtn onPress={navigateBack} />}
      <PageWrapper
        withoutFooter
        testID="sweetpass-membership.payment-method-edit.root.container"
      >
        {isSweetpassPaymentMethodLoading ? (
          <LoadingAnimation testID="sweetpass-membership.payment-method-edit.loading" />
        ) : isSweetpassEnabled ? (
          <SweetpassPaymentMethodEditScreenContent
            testID="sweetpass-membership.payment-method-edit"
            hasActiveSubscription={hasActiveSubscription}
            paymentMethod={paymentMethod}
            pressSetActiveHandler={handlePressSetActive}
            pressRemoveHandler={handlePressRemove}
            isSettingActive={isSettingActive}
            isRemoving={isRemoving}
          />
        ) : null}
      </PageWrapper>
    </TransparentModal>
  );
};
